/**
 * AgendaView — scrolling day-by-day list of events (no grid).
 * Shows upcoming 14 days from the selected date with grouped events.
 */

import React, { useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, SectionList, TouchableOpacity } from 'react-native';
import { DateTime } from 'luxon';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, type ThemeColors } from '../../utils/theme';
import { getPrimaryTagColor } from '../../utils/tagColors';
import { FONT_SIZE, FONT_FAMILY, SPACING, RADIUS } from '../../utils/design-tokens';
import type { CalendarEvent } from '../../types/api';

const AGENDA_DAYS = 14;

interface Props {
  events: CalendarEvent[];
  selectedDate: string;
  onPressEvent?: (event: CalendarEvent) => void;
  onSelectDate?: (date: string) => void;
  tagColors?: Record<string, string> | null;
}

function getEventColor(e: CalendarEvent, tagColors?: Record<string, string> | null): string {
  if (e.type === 'reminder' && e.tags) {
    const tc = getPrimaryTagColor(e.tags, tagColors);
    if (tc) return tc;
  }
  if (e.type === 'google') return '#EA4335';
  if (e.sent) return '#8E8E93';
  if (e.isPaused) return '#FF9500';
  if (e.isRecurring) return '#3478F6';
  return '#34C759';
}

function formatTime(isoUtc: string): string {
  return DateTime.fromISO(isoUtc).toLocal().toFormat('h:mm a');
}

interface Section {
  title: string;
  dateStr: string;
  isToday: boolean;
  data: CalendarEvent[];
}

export default function AgendaView({ events, selectedDate, onPressEvent, onSelectDate, tagColors }: Props) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const todayStr = useMemo(() => DateTime.local().toISODate()!, []);

  const sections: Section[] = useMemo(() => {
    const startDt = DateTime.fromISO(selectedDate);
    const result: Section[] = [];

    for (let i = 0; i < AGENDA_DAYS; i++) {
      const day = startDt.plus({ days: i });
      const dateStr = day.toISODate()!;
      const isToday = dateStr === todayStr;

      const dayEvents = events.filter(e => {
        if (!e.runAtUtc) return false;
        return DateTime.fromISO(e.runAtUtc).toLocal().toISODate() === dateStr;
      }).sort((a, b) => {
        const aT = a.runAtUtc ? DateTime.fromISO(a.runAtUtc).toMillis() : 0;
        const bT = b.runAtUtc ? DateTime.fromISO(b.runAtUtc).toMillis() : 0;
        return aT - bT;
      });

      const label = isToday
        ? `Today — ${day.toFormat('ccc, MMM d')}`
        : day.toFormat('cccc, MMM d');

      result.push({ title: label, dateStr, isToday, data: dayEvents.length > 0 ? dayEvents : [{ body: '', type: 'empty' } as any] });
    }
    return result;
  }, [events, selectedDate, todayStr]);

  const renderItem = useCallback(({ item }: { item: CalendarEvent }) => {
    if ((item as any).type === 'empty') {
      return (
        <View style={s.emptyRow}>
          <Text style={s.emptyText}>No events</Text>
        </View>
      );
    }

    const color = getEventColor(item, tagColors);
    const timeStr = item.runAtUtc ? formatTime(item.runAtUtc) : '';
    const durStr = item.durationMinutes ? `${item.durationMinutes}m` : '';

    return (
      <TouchableOpacity
        style={s.eventRow}
        onPress={() => item.seq ? onPressEvent?.(item) : undefined}
        activeOpacity={0.7}
      >
        <View style={[s.eventAccent, { backgroundColor: color }]} />
        <View style={s.eventContent}>
          <Text style={s.eventBody} numberOfLines={2}>{item.body}</Text>
          <View style={s.eventMeta}>
            {timeStr ? <Text style={s.eventTime}>{timeStr}</Text> : null}
            {durStr ? <Text style={s.eventDuration}>{durStr}</Text> : null}
            {item.isRecurring ? <Ionicons name="repeat" size={12} color={colors.textDim} /> : null}
            {/* Blue G = a FishMemory reminder synced TO Google; Red G = an
                external Google event imported INTO FishMemory. */}
            {(item.source === 'google' || item.type === 'google') ? (
              <Ionicons name="logo-google" size={12} color="#EA4335" accessibilityLabel="From Google Calendar" />
            ) : item.googleEventId ? (
              <Ionicons name="logo-google" size={12} color="#4285F4" accessibilityLabel="Synced to Google Calendar" />
            ) : null}
          </View>
        </View>
      </TouchableOpacity>
    );
  }, [tagColors, onPressEvent, colors, s]);

  const renderSectionHeader = useCallback(({ section }: { section: Section }) => (
    <TouchableOpacity
      style={[s.sectionHeader, section.isToday && s.sectionHeaderToday]}
      onPress={() => onSelectDate?.(section.dateStr)}
      activeOpacity={0.7}
    >
      <Text style={[s.sectionTitle, section.isToday && s.sectionTitleToday]}>{section.title}</Text>
      {section.data.length > 0 && (section.data as any)[0]?.type !== 'empty' && (
        <View style={s.countBadge}>
          <Text style={s.countText}>{section.data.length}</Text>
        </View>
      )}
    </TouchableOpacity>
  ), [onSelectDate, s]);

  return (
    <SectionList
      sections={sections}
      renderItem={renderItem}
      renderSectionHeader={renderSectionHeader}
      keyExtractor={(item, index) => `${item.seq || item.body}-${index}`}
      stickySectionHeadersEnabled
      style={s.container}
      contentContainerStyle={s.contentContainer}
    />
  );
}

function createStyles(colors: ThemeColors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    contentContainer: {
      paddingBottom: 80,
    },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: SPACING.base,
      paddingVertical: SPACING.md,
      backgroundColor: colors.surface,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    sectionHeaderToday: {
      backgroundColor: colors.primaryLight,
    },
    sectionTitle: {
      fontSize: FONT_SIZE.sm,
      fontWeight: '600',
      color: colors.text,
    },
    sectionTitleToday: {
      color: colors.primary,
    },
    countBadge: {
      backgroundColor: colors.primary,
      borderRadius: RADIUS.full,
      paddingHorizontal: SPACING.sm,
      paddingVertical: 2,
    },
    countText: {
      fontSize: FONT_SIZE.xs,
      fontWeight: '600',
      color: '#fff',
    },
    eventRow: {
      flexDirection: 'row',
      paddingVertical: SPACING.md,
      paddingHorizontal: SPACING.base,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    eventAccent: {
      width: 3,
      borderRadius: 2,
      marginRight: SPACING.md,
    },
    eventContent: {
      flex: 1,
    },
    eventBody: {
      fontSize: FONT_SIZE.base,
      color: colors.text,
      fontWeight: '500',
      marginBottom: SPACING.xs,
    },
    eventMeta: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: SPACING.sm,
    },
    eventTime: {
      fontSize: FONT_SIZE.xs,
      color: colors.textSecondary,
    },
    eventDuration: {
      fontSize: FONT_SIZE.xs,
      color: colors.textDim,
    },
    emptyRow: {
      paddingVertical: SPACING.md,
      paddingHorizontal: SPACING['2xl'],
    },
    emptyText: {
      fontSize: FONT_SIZE.sm,
      color: colors.textDim,
      fontStyle: 'italic',
    },
  });
}
